import {
  FilterSelector,
  KeyValue,
} from '@delmonicos/shared-design-system-react/src/components/filter-selector/filter-selector';
import {
  useDebounce,
  usePaginatedFilter,
  usePaginatedFilterControls,
} from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { LocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/location/location.model';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetLocationsPaginatedQuery } from '@service/cpms-bo-location.service';

const LIMIT = 4;

interface LocationFilterControllerProps {
  onChange: (itemIds: string[]) => void;
  selectedItemIds: string[];
}

export const LocationFilterController = ({
  onChange,
  selectedItemIds,
}: LocationFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [search, setSearch, offset, incrementOffset, resetOffset] =
    usePaginatedFilterControls(LIMIT);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isFetching } = useCpmsBoGetLocationsPaginatedQuery({
    limit: LIMIT,
    offset,
    name: debouncedSearch ? `~${debouncedSearch}` : '~',
  });

  const handleMap = useCallback(
    (location: LocationModel): KeyValue => ({
      key: location.id,
      value: location.name,
    }),
    [],
  );

  const { items, updateItems } = usePaginatedFilter<KeyValue>({
    data,
    offset,
    onMap: handleMap,
  });

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setIsTyping(false);
    }
  }, [debouncedSearch]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <FilterSelector
      items={items}
      isLoading={isFetching || isTyping}
      initialSelectedItemIds={selectedItemIds}
      onChange={onChange}
      onSearchChange={(val) => {
        setSearch(val);
        resetOffset();
        updateItems([]);
      }}
      onOffsetChange={incrementOffset}
      labels={{
        title: t('delmonicos-shared-sdk.outputs-transaction.location'),
        search: t('common.filter.search'),
        seeMore: t('common.filter.seeMore'),
      }}
      icon="location"
      hasSeeMore={!!data?.hasNextPage}
    />
  );
};
